<script setup lang="ts">
// @ts-nocheck
import { computed, ref, watch } from "vue";

// Dependencies
import { v4 as uuidv4 } from "uuid";
import { Search } from "@lucide/vue";

// Components
import InputLabel from "./InputLabel.vue";

// ///////////////////
// Props
// ///////////////////

const props = defineProps<{
  value: string | null;
  label?: string;
  placeholder?: string;
  color: string;
  autoComplete?: boolean;
  errorMsg?: string;
  length?: number;
  textOnly?: boolean;
  isSearch?: boolean;
  id?: string;
  searchButtonLabel?: string;
  removeLeadingWhitespace?: boolean;
  required?: boolean;
  isConnectedRight?: boolean;
  focus?: boolean;
  tooltip?: string;
  disabled?: boolean;
  // "sm" renders at 32px (h-8) to align with icon-sm toolbar buttons; default is "md" (40px / h-10).
  size?: "sm" | "md";
  // Accessible name when no visible label is rendered — a placeholder stops naming the field the
  // moment the user types, so label-less usages should pass this.
  ariaLabel?: string;
}>();

// ///////////////////
// Refs
// ///////////////////

const localValue = ref<string | null>(props.value);
const localId = ref<string>(uuidv4());
const inputRef = ref();

// ///////////////////
// Emits
// ///////////////////

const emits = defineEmits(["update:value", "onSearch"]);

// ///////////////////
// Methods
// ///////////////////

const inputChanged = () => {
  //if text only
  if (props.textOnly && /[\d\W]/.test(localValue.value)) localValue.value = localValue.value.replace(/[\d\W]/g, "");

  //if max lenght
  if (props.length !== undefined && localValue.value.length > props.length) {
    localValue.value = localValue.value.slice(0, props.length);
  }

  //if removeLeadingWhitespace
  if (props.removeLeadingWhitespace) localValue.value = localValue.value.trimStart();

  emits("update:value", localValue.value);
};

const handleOnSearch = () => {
  emits("onSearch", localValue.value);
};

// ///////////////////
// Watchers
// ///////////////////

const color = computed(() => {
  if (props.color === "primary") return "focus:border-app-blue-600";
  return undefined;
});

const heightClass = computed(() => (props.size === "sm" ? "h-8" : "h-9"));

watch(
  () => props.focus,
  async (newVal) => {
    if (newVal) {
      inputRef.value?.focus();
    }
  }
);

watch(
  () => props.value,
  async (newVal) => {
    localValue.value = newVal;
  }
);
</script>

<template>
  <div class="flex w-full max-w-lg flex-col">
    <InputLabel
      :id="localId"
      :label="label"
      :required="required"
      :tooltip="tooltip" />
    <div
      v-if="isSearch"
      class="relative flex w-full gap-2">
      <input
        :id="localId ? localId : `input-${localId}`"
        ref="inputRef"
        v-model="localValue"
        type="text"
        :placeholder="placeholder"
        :autocomplete="autoComplete ? 'on' : 'off'"
        :disabled="disabled"
        :aria-label="ariaLabel"
        :aria-invalid="errorMsg ? true : undefined"
        :aria-describedby="errorMsg ? `${localId}-error` : undefined"
        class="focus:border-app-blue-600 focus-ring ring-slate-250 z-10 w-full rounded-lg p-2 pl-9 text-sm text-xs leading-none ring-1"
        :class="[
          color,
          heightClass,
          errorMsg ? 'border-app-red-500' : 'border-slate-300',
          length ? 'pr-[80px]' : $slots.actions ? 'pr-12' : '',
          disabled ? 'text-type-muted cursor-not-allowed bg-slate-200' : 'bg-slate-0',
        ]"
        @input="inputChanged"
        @keydown.enter.prevent="handleOnSearch" />
      <Search
        class="icon-sm text-text-color absolute top-1/2 left-3 z-20 shrink-0 -translate-y-1/2"
        :stroke-width="1.5" />

      <div
        v-if="$slots.actions"
        class="absolute top-1/2 right-1 z-20 -translate-y-1/2">
        <slot name="actions" />
      </div>

      <div
        v-if="length"
        class="text-type-muted absolute top-1/2 right-3 -translate-y-1/2 text-xs">
        {{ value.length }} / {{ length }}
      </div>

      <!--  <Button
        @click="handleOnSearch"
        :label="searchButtonLabel ? searchButtonLabel : 'Search'"
        color="button"
        title="Search"
      /> -->
    </div>

    <div
      v-else
      class="relative w-full">
      <input
        :id="localId ? localId : `input-${localId}`"
        ref="inputRef"
        v-model="localValue"
        type="text"
        :placeholder="placeholder"
        :disabled="disabled"
        :autocomplete="autoComplete ? 'on' : 'off'"
        :aria-label="ariaLabel"
        :aria-invalid="errorMsg ? true : undefined"
        :aria-describedby="errorMsg ? `${localId}-error` : undefined"
        class="focus:border-app-blue-600 w-full rounded-lg border p-2 text-sm leading-none outline-hidden"
        :class="[
          color,
          heightClass,
          errorMsg ? 'border-app-red-500' : 'border-slate-300',
          length ? 'pr-[88px]' : '',
          isConnectedRight ? 'rounded-r-none' : '',
          disabled ? 'text-type-muted cursor-not-allowed bg-slate-200' : 'bg-slate-0',
        ]"
        @keyup.enter="handleOnSearch"
        @input="inputChanged" />
      <div
        v-if="length"
        class="text-type-muted absolute top-1/2 right-4 -translate-y-1/2 text-xs">
        {{ value.length }} / {{ length }}
      </div>
    </div>

    <span
      v-if="errorMsg"
      :id="`${localId}-error`"
      class="text-app-red-700 text-xs"
      >{{ errorMsg }}</span
    >
  </div>
</template>
